import { motion } from 'motion/react';
import { CALL_TO_RISE } from '../../content/site';
import type { EventPhase } from '../../lib/eventState';
import { lineDraw, starPop } from '../../lib/motion';
import { plausibleEventClass } from '../../lib/plausible';

/**
 * "This is your call to rise" — the popup treatment from the 7.15 dev
 * frames: a black rounded box with pointed wing caps (the PopUp ornament,
 * shared with the old privacy bar), display heading, #484848 hairline
 * divider capped with spark stars, then the copy column. The 7.17 revision
 * widened the column to the JoinCallout treatment: the design's exact line
 * breaks are enforced with <br> from lg up (where the vw-scaled type is
 * guaranteed to fit them) and wrap naturally below. `big` paragraphs render
 * the frame's bold 24px emphasis line; **bold** spans are honored.
 *
 * LIVE/POST also carry the WATCH NOW CTA (HX-48, Figma 4552:516 → row node
 * 5726:7431), which takes the tune-in paragraph's slot rather than being
 * appended — see CALL_TO_RISE in content/site.ts.
 */

function Line({ text }: { text: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return <>{parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))}</>;
}

function Paragraph({ lines, big }: { lines: string[]; big?: boolean }) {
  return (
    <p className={big ? 'text-[1.2em] font-bold' : undefined}>
      {lines.map((line, j) => (
        <span key={j}>
          {j > 0 && (
            <>
              {/* The design's break on desktop; a space when wrapping
                  naturally below lg. */}
              <span className="lg:hidden"> </span>
              <br className="hidden lg:inline" />
            </>
          )}
          <Line text={line} />
        </span>
      ))}
    </p>
  );
}

function BoxDivider() {
  return (
    <div aria-hidden="true" className="flex w-full items-center justify-center px-5">
      <span className="relative z-10 flex h-[27px] w-[46px] shrink-0 items-center justify-center">
        <img
          src="/assets/images/ornaments/star.svg"
          alt=""
          className="glow-white-md h-[46px] w-[27px] max-w-none rotate-90"
        />
      </span>
      {/* Line tucks under the stars' bright centers (BreakLine pattern). */}
      <div className="mx-[-22px] h-[2px] min-w-px max-w-[800px] flex-1 bg-[#484848]" />
      <span className="relative z-10 flex h-[27px] w-[46px] shrink-0 items-center justify-center">
        <img
          src="/assets/images/ornaments/star.svg"
          alt=""
          className="glow-white-md h-[46px] w-[27px] max-w-none rotate-90"
        />
      </span>
    </div>
  );
}

/** One side of the CTA: the red hairline running out into its spark star.
 *  `side` sets which end the star caps and which way the line draws. */
function CtaLine({ side }: { side: 'left' | 'right' }) {
  const star = (
    // starPop lives on the SPAN, never the img — the img's rotate-90 is a
    // transform and a motion.img would overwrite it while animating scale.
    <motion.span
      variants={starPop}
      className="relative z-10 flex h-[27px] w-[46px] shrink-0 items-center justify-center"
    >
      <img
        src="/assets/images/ornaments/star-red.svg"
        alt=""
        className="h-[46px] w-[27px] max-w-none rotate-90"
      />
    </motion.span>
  );
  // Negative margin pulls the line under the star's bright center so the two
  // connect (the BreakLine / GlowLineHeading overlap), and the origin is the
  // label side so the line draws outward toward the star.
  const line = (
    <motion.div
      variants={lineDraw}
      className={`h-[2px] w-full bg-brand-red ${
        side === 'left' ? 'ml-[-10px] origin-right' : 'mr-[-10px] origin-left'
      }`}
    />
  );
  return (
    <div className="hidden min-w-4 flex-1 items-center min-[420px]:flex">
      {side === 'left' ? (
        <>
          {star}
          {line}
        </>
      ) : (
        <>
          {line}
          {star}
        </>
      )}
    </div>
  );
}

/**
 * The LIVE/POST watch CTA: red glow lines running out into spark stars either
 * side of the label — the hero's LIVE NOW fixture in red (same lineDraw /
 * starPop entrance from lib/motion, so both play on their section's reveal and
 * both go static under reduced motion).
 *
 * The whole row is the link, as in the POST hero. Glow is ONE filter on the
 * row (.glow-red-cta), which is how the design does it too — it traces the
 * label's letterforms and the line-work in a single pass instead of a
 * text-shadow plus two drop-shadows.
 */
function WatchCta({ href }: { href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={`${CALL_TO_RISE.watchCta} (opens in a new tab)`}
      className={`glow-red-cta group flex w-full max-w-[800px] items-center justify-center gap-[clamp(0.5rem,2vw,1.25rem)] self-center py-[clamp(0.75rem,2vw,1.75rem)] ${plausibleEventClass(
        'CTA Click',
        { location: 'call-to-rise-watch' },
      )}`}
    >
      <CtaLine side="left" />
      {/* 1.9vw hits the design's 36px at its own 1920 frame. The 1.5rem floor
          is deliberately higher than the hosts/heroes headings' 1.15rem: below
          420px the flanking lines drop out (as in the hero), so the label is
          the entire affordance and has to outweigh the 1rem body copy around
          it. Mobile has no designer frame for this box — see HX-19. */}
      <p className="whitespace-nowrap font-heading uppercase leading-none text-brand-red transition-colors duration-200 [font-size:clamp(1.5rem,1.9vw,2.25rem)] group-hover:text-brand-white">
        {CALL_TO_RISE.watchCta}
      </p>
      <CtaLine side="right" />
    </a>
  );
}

export function CallToRise({ phase, watchUrl }: { phase: EventPhase; watchUrl: string }) {
  // The show is on (or done): the tune-in line gives way to the watch CTA.
  const showWatchCta = phase !== 'PRE';

  return (
    <div className="flex w-full items-center justify-center px-2 sm:px-5">
      <img
        src="/assets/images/ornaments/popup-end-1.svg"
        alt=""
        className="w-[clamp(1.5rem,2.8vw,3.36rem)] shrink-0"
      />
      <div className="flex w-full max-w-content flex-col items-center gap-[clamp(0.5rem,1vw,0.75rem)] rounded-[clamp(20px,2.1vw,40px)] bg-black px-5 py-[clamp(1.75rem,3.5vw,3.125rem)] shadow-[0px_0px_20px_0px_rgba(0,0,0,1)]">
        <h2 className="text-glow text-center font-display uppercase leading-none text-brand-white [font-size:clamp(1.8rem,5vw,6rem)]">
          {CALL_TO_RISE.heading}
        </h2>
        <BoxDivider />
        {/* Column intentionally keeps its shrink-to-fit width and default
            stretch alignment — the CTA centers itself (self-center) rather than
            the column re-aligning every paragraph box around it. */}
        <div className="flex max-w-[56rem] flex-col gap-5 text-center font-body leading-normal text-brand-white [font-size:clamp(1rem,1.67vw,1.25rem)]">
          {CALL_TO_RISE.paragraphs.map((p, i) =>
            p.preShowOnly && showWatchCta ? (
              <WatchCta key={i} href={watchUrl} />
            ) : (
              <Paragraph key={i} lines={p.lines} big={p.big} />
            ),
          )}
        </div>
      </div>
      <img
        src="/assets/images/ornaments/popup-end-2.svg"
        alt=""
        className="w-[clamp(1.5rem,2.8vw,3.36rem)] shrink-0 rotate-180"
      />
    </div>
  );
}
